import type { ReactNode } from 'react';

/** Toolbar icons: 16px line drawings in the current text colour. Always paired with a label for assistive technology. */
const Svg = ({ children }: { children: ReactNode }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

const ICONS = {
  draw: (
    <Svg>
      <path d="M3 13l1-3 7-7 2 2-7 7-3 1z" />
      <path d="M9.5 4.5l2 2" />
    </Svg>
  ),
  indicators: (
    <Svg>
      <path d="M2 12c2-5 3-7 5-4s3 2 4-1 2-4 3-4" />
      <path d="M2 14h12" />
    </Svg>
  ),
  templates: (
    <Svg>
      <path d="M8 2l6 3-6 3-6-3 6-3z" />
      <path d="M2 8l6 3 6-3" />
      <path d="M2 11l6 3 6-3" />
    </Svg>
  ),
  oneClick: (
    <Svg>
      <path d="M9 1.5L3.5 9H8l-1 5.5L12.5 7H8l1-5.5z" />
    </Svg>
  ),
  undo: (
    <Svg>
      <path d="M5 3L2 6l3 3" />
      <path d="M2 6h7.5a4 4 0 010 8H7" />
    </Svg>
  ),
  measure: (
    <Svg>
      <path d="M2 11l9-9 3 3-9 9-3-3z" />
      <path d="M5 8l1.5 1.5M7 6l1 1M9 4l1.5 1.5" />
    </Svg>
  ),
  magnet: (
    <Svg>
      <path d="M3 2v6a5 5 0 0010 0V2h-3v6a2 2 0 01-4 0V2H3z" />
      <path d="M3 5h3M10 5h3" />
    </Svg>
  ),
  properties: (
    <Svg>
      <path d="M2 4h7M12 4h2M2 8h2M7 8h7M2 12h9M14 12h0" />
      <circle cx="10.5" cy="4" r="1.5" />
      <circle cx="5.5" cy="8" r="1.5" />
      <circle cx="12.5" cy="12" r="1.5" />
    </Svg>
  ),
  popout: (
    <Svg>
      <path d="M9 2h5v5M14 2L8 8" />
      <path d="M12 9v4a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h4" />
    </Svg>
  ),
  camera: (
    <Svg>
      <path d="M2 5h2.5l1.5-2h4l1.5 2H14v8H2V5z" />
      <circle cx="8" cy="9" r="2.5" />
    </Svg>
  ),
  maximize: (
    <Svg>
      <path d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4" />
    </Svg>
  ),
  restore: (
    <Svg>
      <path d="M6 2v4H2M10 2v4h4M6 14v-4H2M10 14v-4h4" />
    </Svg>
  ),
  zoomIn: (
    <Svg>
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5L14 14M5 7h4M7 5v4" />
    </Svg>
  ),
  zoomOut: (
    <Svg>
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5L14 14M5 7h4" />
    </Svg>
  ),
  latest: (
    <Svg>
      <path d="M3 4l4 4-4 4M8 4l4 4-4 4M14 3v10" />
    </Svg>
  ),
  autoScroll: (
    <Svg>
      <path d="M2 12h9M8 9l3 3-3 3" />
      <path d="M2 4c3 0 4 3 7 3s3-2 5-2" />
    </Svg>
  ),
  bidAsk: (
    <Svg>
      <path d="M2 5h12M2 11h12" strokeDasharray="2 2" />
      <path d="M8 2v3M8 11v3" />
    </Svg>
  ),
  link: (
    <Svg>
      <path d="M7 9a3 3 0 004 0l2-2a3 3 0 00-4-4l-1 1" />
      <path d="M9 7a3 3 0 00-4 0l-2 2a3 3 0 004 4l1-1" />
    </Svg>
  ),
  trades: (
    <Svg>
      <path d="M4 13V6M2 8l2-2 2 2" />
      <path d="M12 3v7M10 8l2 2 2-2" />
    </Svg>
  ),
  more: (
    <Svg>
      <circle cx="3.5" cy="8" r="1" fill="currentColor" />
      <circle cx="8" cy="8" r="1" fill="currentColor" />
      <circle cx="12.5" cy="8" r="1" fill="currentColor" />
    </Svg>
  ),
} satisfies Record<string, ReactNode>;

export type IconName = keyof typeof ICONS;

export function Icon({ name }: { name: IconName }) {
  return ICONS[name];
}
